// Engagement-letter compliance state on a matter.
//
// Mirrors matters.engagement_letter (jsonb) from migration
// 20260521000001_engagement_letter. The engagement letter is the written
// fee/services agreement required by the Migration Agents Code of Conduct 2021
// reg. 6 + 14. A matter cannot lodge until the letter is signed or on file.

export type EngagementLetterStatus = 'not_sent' | 'sent' | 'signed' | 'on_file';

export type EngagementLetterChannel = 'docusign' | 'paper' | 'ghl' | 'other';

export interface EngagementLetterState {
  status: EngagementLetterStatus;
  sent_at: string | null;
  signed_at: string | null;
  filed_at: string | null;
  recorded_by_staff_id: string | null;
  channel: EngagementLetterChannel | null;
  external_ref: string | null;
  document_id: string | null;
  note: string | null;
}

// A NULL engagement_letter (existing matters predating the column default) is
// treated as not_sent.
export const DEFAULT_ENGAGEMENT_LETTER: EngagementLetterState = {
  status: 'not_sent',
  sent_at: null,
  signed_at: null,
  filed_at: null,
  recorded_by_staff_id: null,
  channel: null,
  external_ref: null,
  document_id: null,
  note: null,
};

/** Normalise a possibly-null DB value into a concrete state object. */
export function normaliseEngagementLetter(
  value: EngagementLetterState | null | undefined,
): EngagementLetterState {
  if (!value || typeof value !== 'object') return { ...DEFAULT_ENGAGEMENT_LETTER };
  return { ...DEFAULT_ENGAGEMENT_LETTER, ...value };
}

/**
 * Whether the engagement letter satisfies the pre-lodgement requirement.
 * True only when an actual signed PDF is on file: status 'on_file' AND a
 * document_id is present. A status tick without the held document is not
 * enough (MARA reg. 14 requires keeping a copy). Matches
 * fn_check_engagement_letter_before_lodgement in the DB.
 */
export function canLodge(
  value: EngagementLetterState | null | undefined,
): boolean {
  const s = normaliseEngagementLetter(value);
  return s.status === 'on_file' && !!s.document_id;
}

export const ENGAGEMENT_LETTER_STATUS_LABEL: Record<EngagementLetterStatus, string> = {
  not_sent: 'Not sent',
  sent: 'Sent',
  signed: 'Signed',
  on_file: 'On file',
};

export const ENGAGEMENT_LETTER_CHANNEL_LABEL: Record<EngagementLetterChannel, string> = {
  docusign: 'DocuSign',
  paper: 'Paper',
  ghl: 'GHL',
  other: 'Other',
};
